
const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

import {
    ApplicationUserModel,
    AuditExplanationModel,
    AuditExplanationTypeEnum,
    AuditModel,
    AuditWorkplaceModel,
    CategoryModel,
    DefectModel,
    DefectStationModel,
    DefectWorkplaceModel,
    EscalationGroupTypeEnum,
    EscalationTypeEnum,
    FileModel,
    FindingManagerModel,
    FindingModel,
    ImplementationStateEnum,
    MachineComponentModel,
    MachineComponentStationModel,
    MachineComponentWorkplaceModel,
    PermissionTypeEnum,
    PlanItemFindingManagerModel,
    PlanItemModel,
    PlanItemRelationModel,
    PlanItemRelationTypeEnum,
    ProcessStepModel,
    ProcessTypeEnum,
    QuestionFileModel,
    QuestionGroupModel,
    QuestionModel,
    ResponseFileModel,
    ResponseFileType,
    ResponseModel,
    ResponseTypeEnum,
    ResponseValueModel,
    StationModel,
    TenantUserModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { questionGroupsStore } from '@/libs/question-groups/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { QuestionContainer, QuestionGroupResponse, ResponseContainer } from '../model/QuestionGroupResponse';
import { Guid } from '@/libs/common/functions/guid';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { cloneDeep, isEqual } from 'lodash';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import QuestionFileItem from '@/libs/audits/components/QuestionFileItem.vue';
import QuestionFileDetail from '@/libs/audits/components/QuestionFileDetail.vue';
import { categoriesStore } from '@/libs/categories/+state/store';
import { auditsStore } from '@/libs/audits/+state/store';
import { ProcessStepTypeEnum, VDA63, Vda63QuestionModel } from '@/audit-settings/VDA63';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { SystemAuditValueEnum } from '@/libs/core/models/system-audit-value-enum';
import CustomerSelect from '@/libs/common/components-smart/CustomerSelect.vue';
import WorkTaskSummary from '@/libs/work-tasks/components/WorkTaskSummary.vue';
import WorkTaskVda63Summary from '@/libs/work-tasks/components/WorkTaskVda63Summary.vue';
import WorkTaskEscalation from '@/libs/work-tasks/components/WorkTaskEscalation.vue';
import WorkTaskEndShutdown from '@/libs/work-tasks/components/WorkTaskEndShutdown.vue';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import WorkTaskEditShutdownTime from '@/libs/work-tasks/components/WorkTaskEditShutdownTime.vue';
import ProductSelect from '@/libs/common/components-smart/ProductSelect.vue';
import ProjectSelect from '@/libs/common/components-smart/ProjectSelect.vue';
import InstructionFilePreview from '@/libs/work-tasks/components/InstructionFilePreview.vue';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import { stationsStore } from '@/libs/stations/+state/store';
import moment from 'moment';
import CustomFieldGroupForm from '@/libs/common/components/CustomFieldGroupForm.vue';
import { isMobile } from '@syncfusion/ej2-vue-schedule';
import WorkTaskPrintButton from '@/libs/work-tasks/components/WorkTaskPrintButton.vue';
import { defectsStore, defectWorkplacesStore, defectStationsStore } from '@/libs/defects/+state/store';
import {
    machineComponentsStore,
    machineComponentWorkplacesStore,
    machineComponentStationsStore,
} from '@/libs/machine-components/+state/store';

@Component({
    components: {
        CustomFieldGroupForm,
        InstructionFilePreview,
        ProductSelect,
        ProjectSelect,
        WorkTaskEditShutdownTime,
        WorkTaskEndShutdown,
        WorkTaskEscalation,
        WorkTaskVda63Summary,
        WorkTaskSummary,
        CustomerSelect,
        ShiftTypeSelect,
        WorkTaskDialogFiles,
        QuestionFileItem,
        QuestionFileDetail,
        WorkTaskPrintButton,
    },
})
export default class WorkTaskAuditTab extends Page {
    valid = true;
    questionGroups: Array<QuestionGroupModel> = [];
    items: Array<QuestionGroupResponse> = [];
    itemsOriginal: Array<QuestionGroupResponse> = [];
    loading = true;
    item: PlanItemModel | null = null;
    category: CategoryModel | null = null;
    audit: AuditModel | null = null;
    instructionsDialog = false;
    processType: ProcessTypeEnum = ProcessTypeEnum.Audit;
    findingManagers: PlanItemFindingManagerModel[] = [];
    // printing handled by WorkTaskPrintButton
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected EscalationGroupTypeEnum: typeof EscalationGroupTypeEnum = EscalationGroupTypeEnum;
    protected PlanItemRelationTypeEnum: typeof PlanItemRelationTypeEnum = PlanItemRelationTypeEnum;

    showResponseValue: string | null = null;
    currentQuestionFile: any = {
        questionGroupIndex: 0,
        questionIndex: 0,
        fileIndex: 0,
        canPrev: false,
        canNext: false,
        file: null,
    };
    correctionManagersRules = [(v: any[]) => (!!v && v.length > 0) || this.$i18n.t('common.thisFileIsRequired')];
    showAllDefects = true;
    showAllMachineComponents = true;

    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    planItemSubscription: any = null;

    @Ref() questionFileDetail!: QuestionFileDetail;
    @Ref() auditFileDetail!: QuestionFileDetail;
    @Prop() model!: PlanItemModel;
    @Prop() categoryModel!: CategoryModel;
    @Prop() id!: string;
    @Prop() auditId!: string;
    @Prop() auditModel!: AuditModel;
    @Prop({ default: -1 }) minimumImageDate!: number;

    @Ref() form!: any;
    @Ref() editShutdownDialog!: WorkTaskEditShutdownTime;

    get isVda63(): boolean {
        return this.audit?.category?.responseType == ResponseTypeEnum.VDA63;
    }

    get isIssueSolving(): boolean {
        return (
            this.audit?.category?.processType == ProcessTypeEnum.Maintenance ||
            this.audit?.category?.processType == ProcessTypeEnum.Checklist ||
            this.audit?.category?.processType == ProcessTypeEnum.ProductInspection ||
            this.audit?.category?.processType == ProcessTypeEnum.IssueSolving
        );
    }

    get isIssueSolvingProcessType(): boolean {
        return this.audit?.category?.processType == ProcessTypeEnum.IssueSolving;
    }

    get isProductAudit(): boolean {
        return this.audit?.category?.responseType == ResponseTypeEnum.ProductAudit;
    }

    get canShowWaitingQuestionOption() {
        return this.item != null && this.item.responseLock == false;
    }

    get hasCorrectQueryParam(): boolean {
        return this.$route.query.correct?.toString() == '1';
    }

    get canEditEvidenceNumber(): boolean {
        if (!this.category?.useAutoEvidenceNumber) {
            return true;
        } else {
            return this.item?.responseLock ?? true;
        }
    }

    get canEditWorkplace() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return (
            (this.item != null &&
                ((this.item.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.item.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.item.responseLock == false) ||
            (permissions.contains(PermissionTypeEnum.MistakeCorrection) && hasCorrectQueryParam) ||
            (permissions.contains(PermissionTypeEnum.ResponseCorrection) && !this.item!.responseLock)
        );
    }

    get canEditQuestions() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return (
            (this.item != null &&
                ((this.item.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.item.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.item.responseLock == false) ||
            (permissions.contains(PermissionTypeEnum.MistakeCorrection) && hasCorrectQueryParam) ||
            (permissions.contains(PermissionTypeEnum.ResponseCorrection) && !this.item!.responseLock)
        );
    }

    get canEditWorkplaceId(): boolean {
        return this.getQuery('workplaceId') == null;
    }

    get canEditFiles() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return (
            (this.item != null &&
                ((this.item.auditTenantUsers?.some((x) => x.tenantUserId == this.tenantUser.id) &&
                    this.category?.processType != ProcessTypeEnum.IssueSolving) ||
                    (this.item.tenantUserId == this.tenantUser.id &&
                        this.category?.processType == ProcessTypeEnum.IssueSolving)) &&
                this.item.responseLock == false) ||
            (permissions.contains(PermissionTypeEnum.MistakeCorrection) && hasCorrectQueryParam) ||
            (permissions.contains(PermissionTypeEnum.ResponseCorrection) && !this.item!.responseLock)
        );
    }

    get canShowCorrectiveMeasures() {
        return this.item != null && this.item.responseLock == true;
    }

    get canEditCorrectiveMeasurements() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return (
            (this.item != null &&
                this.item.responseLock == true &&
                this.item.fullLock == false &&
                (permissions.contains(PermissionTypeEnum.Findings) ||
                    permissions.contains(PermissionTypeEnum.FindingManager))) ||
            (permissions.contains(PermissionTypeEnum.MistakeCorrection) && hasCorrectQueryParam)
        );
    }

    get storedPlanItem(): PlanItemModel {
        return planItemsStore.useGetter(CrudGetter.Detail) as PlanItemModel;
    }

    get requiredRules() {
        return [(v: any) => !!v || this.$t('validation.fieldIsRequired')];
    }

    get processElements(): string[] {
        const processElements = this.questionGroups
            .where((x) => x.processElement != null)
            .groupBy((x) => x.processElement!)
            .select((x) => x.first().processElement!)
            .distinct()
            .toArray();
        return VDA63.ProcessElements.where((x) => processElements.contains(x)).toArray();
    }

    get newId() {
        return Guid.EmptyGuid();
    }

    get auditWorkplaceIds(): string[] {
        if (this.audit?.useWorkplaceLimitation == true) {
            const auditWorkplaces = (auditWorkplacesStore.useGetter(CrudGetter.Data, 'WorkTaskDialog') ??
                []) as AuditWorkplaceModel[];
            return auditWorkplaces.map((x) => x.workplaceId!);
        }
        return [];
    }

    get stationsFilter(): string[] {
        let auditStations: string[] = [];
        const stations: StationModel[] = stationsStore.useGetter(CrudGetter.Data) ?? [];
        if (this.audit?.useWorkplaceLimitation == true) {
            const auditWorkplaces = (auditWorkplacesStore.useGetter(CrudGetter.Data, 'WorkTaskDialog') ??
                []) as AuditWorkplaceModel[];
            for (const auditWorkplace of auditWorkplaces) {
                if (auditWorkplace.useStationLimitation == true) {
                    auditStations = [
                        ...auditStations,
                        ...(auditWorkplace.auditWorkplaceStations ?? []).map((x) => x.stationId!),
                    ];
                } else {
                    auditStations = [
                        ...auditStations,
                        ...stations.filter((x) => x.workplaceId == auditWorkplace.workplaceId).map((x) => x.id!),
                    ];
                }
            }
        }
        let stationsFilter: string[] = [];
        try {
            stationsFilter = this.getQuery('stations') ? JSON.parse(this.getQuery('stations') ?? '[]') : [];
        } catch (e) {
            console.error(e);
        }
        if (auditStations.length > 0) {
            return auditStations.filter((x) => stationsFilter.contains(x));
        } else {
            return stationsFilter;
        }
    }

    get customFieldGroupsToShow() {
        if (!this.audit || !this.category) return [];
        const usage = this.audit.customFieldUsageType;
        if (usage === 'ByCategory') {
            return this.category.categoryCustomFieldGroups || [];
        } else if (usage === 'ByAudit') {
            return this.audit.auditCustomFieldGroups || [];
        } else if (usage === 'ByCategoryAndAudit') {
            return [...(this.category.categoryCustomFieldGroups || []), ...(this.audit.auditCustomFieldGroups || [])];
        }
        return [];
    }

    get isMobileOrTablet(): boolean {
        return this.$vuetify.breakpoint.width < 991;
    }

    get isTablet(): boolean {
        return this.$vuetify.breakpoint.width >= 576 && this.$vuetify.breakpoint.width < 991;
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 576;
    }

    get canCorrectMistakes() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        return permissions.contains(PermissionTypeEnum.MistakeCorrection);
    }

    get canRemovePlanItem() {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        return permissions.contains(PermissionTypeEnum.MistakeCorrection);
    }

    get availableDefects(): DefectModel[] {
        if (!this.item?.workplaceId) return [];

        const allDefects = (defectsStore.useGetter(CrudGetter.Data) as DefectModel[]) ?? [];
        const availableDefectIds = new Set<string>();

        // Načíst defekty pro pracoviště
        const defectWorkplaces = (defectWorkplacesStore.useGetter(CrudGetter.Data) as DefectWorkplaceModel[]) ?? [];
        defectWorkplaces
            .filter((dw) => dw.workplaceId === this.item!.workplaceId)
            .forEach((dw) => {
                if (dw.defectId) {
                    availableDefectIds.add(dw.defectId);
                }
            });

        // Pokud je vybrané stanoviště, přidat i defekty pro stanoviště
        if (this.item.stationId) {
            const defectStations = (defectStationsStore.useGetter(CrudGetter.Data) as DefectStationModel[]) ?? [];
            defectStations
                .filter((ds) => ds.stationId === this.item!.stationId)
                .forEach((ds) => {
                    if (ds.defectId) {
                        availableDefectIds.add(ds.defectId);
                    }
                });
        }

        // Vrátit sjednocené defekty seřazené podle kódu
        return allDefects
            .filter((d) => d.id && availableDefectIds.has(d.id))
            .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
    }

    get availableMachineComponents(): MachineComponentModel[] {
        if (!this.item?.workplaceId) return [];

        const allComponents = (machineComponentsStore.useGetter(CrudGetter.Data) as MachineComponentModel[]) ?? [];
        const availableComponentIds = new Set<string>();

        // Načíst komponenty pro pracoviště
        const componentWorkplaces =
            (machineComponentWorkplacesStore.useGetter(CrudGetter.Data) as MachineComponentWorkplaceModel[]) ?? [];
        componentWorkplaces
            .filter((cw) => cw.workplaceId === this.item!.workplaceId)
            .forEach((cw) => {
                if (cw.machineComponentId) {
                    availableComponentIds.add(cw.machineComponentId);
                }
            });

        // Pokud je vybrané stanoviště, přidat i komponenty pro stanoviště
        if (this.item.stationId) {
            const componentStations =
                (machineComponentStationsStore.useGetter(CrudGetter.Data) as MachineComponentStationModel[]) ?? [];
            componentStations
                .filter((cs) => cs.stationId === this.item!.stationId)
                .forEach((cs) => {
                    if (cs.machineComponentId) {
                        availableComponentIds.add(cs.machineComponentId);
                    }
                });
        }

        // Vrátit sjednocené komponenty seřazené podle kódu
        return allComponents
            .filter((c) => c.id && availableComponentIds.has(c.id))
            .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
    }

    hasFindings(): boolean {
        for (const i of this.items) {
            for (const qc of i.questionContainers) {
                for (const response of qc.responses) {
                    if (!this.audit?.useIssueSolvingEscalation && response.findings && response.findings.length > 0) {
                        return true;
                    } else if (this.audit?.useIssueSolvingEscalation && response.hasDisagreement) {
                        if (!this.useAutoIssueEscalation(response)) {
                            return true;
                        }
                    } else if (
                        this.audit?.useIssueSolvingEscalation &&
                        !this.useAutoIssueEscalation(response) &&
                        response.findings &&
                        response.findings.length > 0
                    ) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    @Watch('items', { deep: true })
    itemsChanged() {
        this.$emit('change');
    }

    @Watch('model')
    modelChanged(v: PlanItemModel | null) {
        this.item = v;
    }

    @Watch('item')
    itemChanged(v: PlanItemModel | null) {
        this.$emit('update:model', v);
        // Reset stavu zobrazení při změně položky
        this.showAllDefects = !v?.defectId;
        this.showAllMachineComponents = !v?.machineComponentId;
    }

    @Watch('item.defectId')
    defectIdChanged(newVal: string | null) {
        if (newVal) {
            this.showAllDefects = false;
        }
    }

    @Watch('item.machineComponentId')
    machineComponentIdChanged(newVal: string | null) {
        if (newVal) {
            this.showAllMachineComponents = false;
        }
    }

    @Watch('categoryModel')
    categoryModelChanged(v: CategoryModel | null) {
        this.category = v;
    }

    @Watch('category')
    categoryChanged(v: CategoryModel | null) {
        this.$emit('update:categoryModel', v);
    }

    @Watch('loading')
    loadingChanged(v: boolean, p: boolean) {
        if (!v && p) {
            this.$emit('loadingFinished');
        }
    }

    @Watch('item.workplaceId')
    workplaceIdChanged(newVal: string | null, oldVal: string | null) {
        if (newVal !== oldVal && this.category?.processType == ProcessTypeEnum.IssueSolving) {
            this.loadDefectsAndComponents();
        }
    }

    @Watch('item.stationId')
    stationIdChanged(newVal: string | null, oldVal: string | null) {
        if (newVal !== oldVal && this.category?.processType == ProcessTypeEnum.IssueSolving) {
            this.loadDefectsAndComponents();
        }
    }

    canEditImplementationOfCorrectiveMeasurements(response: ResponseModel) {
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        const hasCorrectQueryParam = this.$route.query?.correct?.toString() == '1';
        return (
            (this.item != null &&
                this.item.responseLock == true &&
                this.item.fullLock == false &&
                (permissions.contains(PermissionTypeEnum.Measures) ||
                    permissions.contains(PermissionTypeEnum.MeasuresAllUsers)) &&
                response.implementerUserId == this.tenantUser.id!) ||
            (permissions.contains(PermissionTypeEnum.MistakeCorrection) && hasCorrectQueryParam)
        );
    }

    canShowNotRatedQuestionOption(response: ResponseModel) {
        if (this.category!.useNotRatedOption) {
            return true;
        } else if (
            (this.category!.responseType == 'Ok' ||
                this.category!.responseType == 'OkNok' ||
                this.category!.responseType == 'OkNokWarning' ||
                this.category!.responseType == 'NokWarning') &&
            response.okValue == -1
        ) {
            return true;
        } else if (this.category!.responseType == 'SystemAudit' && response.systemAuditValue == -1) {
            return true;
        } else if (this.category!.responseType == 'ProductAudit' && response.productAuditValue == -1) {
            return true;
        } else if (
            (this.category!.responseType == 'ScoreZeroToOne' ||
                this.category!.responseType == 'ScoreZeroToTwo' ||
                this.category!.responseType == 'ScoreOneToThree' ||
                this.category!.responseType == 'ScoreZeroToFour' ||
                this.category!.responseType == 'ScoreZeroToFive' ||
                this.category!.responseType == 'ScoreOneToFive') &&
            response.scoreValue == -1
        ) {
            return true;
        }
        return false;
    }

    isShowNotRatedQuestionOption(response: ResponseModel) {
        if (
            (this.category!.responseType == 'Ok' ||
                this.category!.responseType == 'OkNok' ||
                this.category!.responseType == 'OkNokWarning' ||
                this.category!.responseType == 'NokWarning') &&
            response.okValue == -1
        ) {
            return true;
        } else if (this.category!.responseType == 'SystemAudit' && response.systemAuditValue == -1) {
            return true;
        } else if (this.category!.responseType == 'ProductAudit' && response.productAuditValue == -1) {
            return true;
        } else if (
            (this.category!.responseType == 'ScoreZeroToOne' ||
                this.category!.responseType == 'ScoreZeroToTwo' ||
                this.category!.responseType == 'ScoreOneToThree' ||
                this.category!.responseType == 'ScoreZeroToFour' ||
                this.category!.responseType == 'ScoreZeroToFive' ||
                this.category!.responseType == 'ScoreOneToFive' ||
                this.category!.responseType == 'VDA63') &&
            response.scoreValue == -1
        ) {
            return true;
        }
        return false;
    }

    mounted() {
        this.loading = true;
        if (this.id && this.id != Guid.EmptyGuid() && !this.model) {
            this.loadPlanItem()
                .then((item: PlanItemModel) => {
                    this.processType = item.processType!;
                    this.loadAudit(item.auditId!)
                        .then((e: AuditModel) => {
                            this.audit = e;
                            this.loadCategory(this.audit.categoryId!)
                                .then((c: CategoryModel) => {
                                    this.category = c;
                                    this.loadQuestionGroups()
                                        .then(() => {
                                            this.loadAuditWorkplaces()
                                                .then(() => {
                                                    if (c.processType == ProcessTypeEnum.IssueSolving) {
                                                        this.loadDefectsAndComponents();
                                                    }
                                                    this.make();
                                                    this.loading = false;
                                                })
                                                .catch((e) => console.log(e));
                                        })
                                        .catch((e: any) => {
                                            console.log(e);
                                        });
                                })
                                .catch((e) => {
                                    console.log(e);
                                });
                        })
                        .catch((e) => {
                            console.log(e);
                        });
                })
                .catch((e) => {
                    console.log(e);
                });
        } else {
            this.item = this.model;
            this.category = this.categoryModel;
            this.audit = this.auditModel;
            this.loadQuestionGroups()
                .then((e) => {
                    this.loadAudit(this.auditId!)
                        .then((a) => {
                            this.loadAuditWorkplaces()
                                .then(() => {
                                    this.processType = a.processType!;
                                    this.audit = a;
                                    if (this.category?.processType == ProcessTypeEnum.IssueSolving) {
                                        this.loadDefectsAndComponents();
                                    }
                                    this.make();
                                    this.loading = false;
                                })
                                .catch((e) => console.log(e));
                        })
                        .catch((e) => {
                            console.log(e);
                        });
                })
                .catch((e: any) => {
                    console.log(e);
                });
        }
        this.planItemSubscription = this.$store.subscribe((mutation, _state) => {
            if (
                mutation.type === planItemsStore.getActionName(CrudResponseAction.PartialUpdateSuccess) ||
                mutation.type === planItemsStore.getActionName(CrudResponseAction.UpdateSuccess)
            ) {
                const planItem = (mutation.payload.item ?? mutation.payload.itemBasic ?? {}) as PlanItemModel;
                if (this.item?.id == planItem.id) {
                    this.item = {
                        ...this.item,
                        ...planItem,
                    };
                }
            }
        });
    }

    unmounted() {
        this.planItemSubscription?.unsubscribe();
    }

    public refresh() {
        this.loadPlanItem()
            .then(() => {
                this.loadAudit(this.auditId!)
                    .then((e: AuditModel) => {
                        this.audit = e;
                        this.loadCategory(this.audit.categoryId!)
                            .then((e: CategoryModel) => {
                                this.category = e;
                                this.loadQuestionGroups()
                                    .then((e) => {
                                        this.make();
                                        this.loading = false;
                                    })
                                    .catch((e: any) => {
                                        console.log(e);
                                    });
                            })
                            .catch((e) => {
                                console.log(e);
                            });
                    })
                    .catch((e: any) => {
                        console.log(e);
                    });
            })
            .catch((e) => {
                console.log(e);
            });
    }

    isChanged() {
        return !isEqual(this.itemsOriginal, this.items);
    }

    showResponseValues(value: string) {
        if (value != this.showResponseValue) this.showResponseValue = value;
        else this.showResponseValue = null;
    }

    loadPlanItem(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            planItemsStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(planItemsStore, CrudReponse.Get)
                .then((e: PlanItemModel) => {
                    this.item = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadCategory(categoryId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            categoriesStore.dispatch(CrudAction.Get, { id: categoryId });
            this.subscribe(categoriesStore, CrudReponse.Get)
                .then((e: CategoryModel) => {
                    this.category = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadAudit(auditId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            auditsStore.dispatch(CrudAction.Get, { id: auditId });
            this.subscribe(auditsStore, CrudReponse.Get)
                .then((e: CategoryModel) => {
                    this.audit = e as any;
                    // Initialize planItem process/product groups from template if not using performance-time definition
                    try {
                        const useAtPerformance = (this.audit as any)?.useVdaDefinitionAtPerformance == true;
                        if (!useAtPerformance) {
                            const planItem = this.item!;
                            if (!Array.isArray(planItem.planItemProcessSteps)) planItem.planItemProcessSteps = [];
                            if (!Array.isArray(planItem.planItemProductGroups)) planItem.planItemProductGroups = [];
                            // Copy visible process steps from audit
                            const visibleSteps =
                                (this.audit as any)?.processSteps?.filter((x: any) => x && x.isHidden != true) ?? [];
                            if ((planItem.planItemProcessSteps?.length ?? 0) === 0 && visibleSteps.length > 0) {
                                planItem.planItemProcessSteps = visibleSteps
                                    .orderBy((x: any) => x.position)
                                    .toArray()
                                    .map((s: any) => ({ id: Guid.NewGuid(), processStepId: s.id, processStep: s }));
                            }
                            // Copy product groups if enabled
                            if ((this.audit as any)?.useProductGroups == true) {
                                const auditGroups = (this.audit as any)?.productGroups ?? [];
                                if ((planItem.planItemProductGroups?.length ?? 0) === 0 && auditGroups.length > 0) {
                                    planItem.planItemProductGroups = auditGroups
                                        .orderBy((x: any) => x.position)
                                        .toArray()
                                        .map((g: any) => ({ id: Guid.NewGuid(), productGroupId: g.id, productGroup: g }));
                                }
                                // Copy productGroupProcessSteps links
                                if (!Array.isArray((planItem as any).productGroupProcessSteps))
                                    (planItem as any).productGroupProcessSteps = [];
                                const auditLinks = (this.audit as any)?.productGroupProcessSteps ?? [];
                                if (((planItem as any).productGroupProcessSteps?.length ?? 0) === 0 && auditLinks.length > 0) {
                                    const planSteps = (planItem.planItemProcessSteps ?? []).map((p: any) => p.processStep);
                                    const planGroups = (planItem.planItemProductGroups ?? []).map((p: any) => p.productGroup);
                                    const stepByKey: any = {};
                                    const groupByKey: any = {};
                                    planSteps.forEach((s: any) => (stepByKey[s.key] = s));
                                    planGroups.forEach((g: any) => (groupByKey[g.key] = g));
                                    planItem!.productGroupProcessSteps = auditLinks
                                        .filter((l: any) => l?.processStep && l.processStep.isHidden != true)
                                        .map((l: any) => ({
                                            id: Guid.NewGuid(),
                                            productGroupId: l.productGroupId,
                                            processStepId: l.processStepId,
                                            productGroup:
                                                groupByKey[l.productGroup?.key] ??
                                                planGroups.firstOrDefault((x: any) => x.id == l.productGroupId) ??
                                                l.productGroup,
                                            processStep:
                                                stepByKey[l.processStep?.key] ??
                                                planSteps.firstOrDefault((x: any) => x.id == l.processStepId) ??
                                                l.processStep,
                                            position: l.position ?? 0,
                                        }));
                                }
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    public responseChanged(response: ResponseModel) {
        let responseValue: ResponseValueModel | null = null;
        if (
            this.category!.responseType == ResponseTypeEnum.Ok ||
            this.category!.responseType == ResponseTypeEnum.OkNok ||
            this.category!.responseType == ResponseTypeEnum.OkNokWarning ||
            this.category!.responseType == ResponseTypeEnum.NokWarning
        ) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.okValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.systemAuditValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.productAuditValue) ?? null;
        } else {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.scoreValue) ?? null;
        }
        response.findings = (response.findings ?? []).filter((x) => {
            const cause = x.cause as string | null;
            const isEmpty =
                x.id == Guid.EmptyGuid() &&
                (x.description == null || x.description == '') &&
                (cause == null || cause == '') &&
                (x.findingManagers ?? []).length == 0 &&
                (x.findingFiles ?? []).length == 0 &&
                !x.useDefinedManagers &&
                (x.measures ?? []).length == 0;
            return !isEmpty;
        });
        if (responseValue == null) {
            response.isOk = null;
            response.immediateMeasures = null;
            response.escalationDescription = null;
        } else if (responseValue?.escalationType == EscalationTypeEnum.RequiredEscalation) {
            response.isOk = false;
            response.immediateMeasures = '';
            response.findings = [
                {
                    id: Guid.EmptyGuid(),
                    description: null,
                    cause: null,
                    useDefinedManagers: this.category && this.category.mustSelectFindingManager,
                    findingManagers: [],
                    findingFiles: [],
                    processUntil:
                        this.category?.useFindingProcessUntil && this.category.findingDefaultProcessDays
                            ? moment().add(this.category.findingDefaultProcessDays, 'days').format('YYYY-MM-DDTHH:mm:ss')
                            : null,
                } as FindingModel,
            ];
        } else if (responseValue?.escalationType == EscalationTypeEnum.OptionalEscalation) {
            response.isOk = true;
            response.immediateMeasures = null;
            response.escalationDescription = null;
        } else {
            response.isOk = true;
            response.immediateMeasures = null;
            response.escalationDescription = null;
        }
        if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            if (
                responseValue!.value == SystemAuditValueEnum.Compliant ||
                responseValue!.value == SystemAuditValueEnum.OportunityForImprovement ||
                responseValue!.value == SystemAuditValueEnum.PositiveAspect
            ) {
                response.isOk = true;
            } else if (
                responseValue!.value == SystemAuditValueEnum.MajorNotCompliant ||
                responseValue!.value == SystemAuditValueEnum.MinorNotCompliant
            ) {
                response.isOk = false;
            }
        }
    }

    public canEscalate(response: ResponseModel) {
        let responseValue: ResponseValueModel | null = null;
        if (
            this.category!.responseType == ResponseTypeEnum.Ok ||
            this.category!.responseType == ResponseTypeEnum.OkNok ||
            this.category!.responseType == ResponseTypeEnum.OkNokWarning ||
            this.category!.responseType == ResponseTypeEnum.NokWarning
        ) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.okValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.systemAuditValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.productAuditValue) ?? null;
        } else {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.scoreValue) ?? null;
        }
        return responseValue?.escalationType == EscalationTypeEnum.OptionalEscalation;
    }

    public escalate(response: ResponseModel) {
        response.isOk = false;
        response.findings = [
            {
                id: Guid.EmptyGuid(),
                description: null,
                cause: null,
                useDefinedManagers: this.category && this.category.mustSelectFindingManager,
                findingManagers: [],
                findingFiles: [],
            } as FindingModel,
        ];
    }

    public cancelEscalation(response: ResponseModel) {
        response.isOk = true;
        response.findings = [];
    }

    public plainToHtml(value: string | null | undefined) {
        return value?.replaceAll('\n', '<br />');
    }

    showQuestionFileDetail(questionGroupIndex: number, questionIndex: number, fileIndex: number) {
        this.currentQuestionFile = {
            questionGroupIndex: questionGroupIndex,
            questionIndex: questionIndex,
            fileIndex: fileIndex,
            canPrev: fileIndex > 0 ? true : false,
            canNext:
                fileIndex + 1 < this.questionGroups[questionGroupIndex].questions![questionIndex].questionFiles!.length
                    ? true
                    : false,
            file: this.questionGroups[questionGroupIndex].questions![questionIndex].questionFiles![fileIndex],
        };
        if (this.currentQuestionFile) this.questionFileDetail.open(this.currentQuestionFile);
    }

    prevQuestionFile() {
        let targetFile: QuestionFileModel | null = null;
        targetFile =
            this.questionGroups[this.currentQuestionFile.questionGroupIndex].questions![this.currentQuestionFile.questionIndex]
                .questionFiles![this.currentQuestionFile.fileIndex - 1];
        if (targetFile) {
            this.currentQuestionFile.file = targetFile;
            this.currentQuestionFile.fileIndex--;
            this.currentQuestionFile.canPrev = this.currentQuestionFile.fileIndex > 0 ? true : false;
            this.currentQuestionFile.canNext = true;
        }
    }

    nextQuestionFile() {
        let targetFile: QuestionFileModel | null = null;
        targetFile =
            this.questionGroups[this.currentQuestionFile.questionGroupIndex].questions![this.currentQuestionFile.questionIndex]
                .questionFiles![this.currentQuestionFile.fileIndex + 1];
        if (targetFile) {
            this.currentQuestionFile.file = targetFile;
            this.currentQuestionFile.fileIndex++;
            this.currentQuestionFile.canPrev = this.currentQuestionFile.fileIndex > 0 ? true : false;
            this.currentQuestionFile.canNext =
                this.currentQuestionFile.fileIndex + 1 <
                this.questionGroups[this.currentQuestionFile.questionGroupIndex].questions![
                    this.currentQuestionFile.questionIndex
                ].questionFiles!.length
                    ? true
                    : false;
        }
    }

    showAuditFileDetail(fileIndex: number) {
        this.currentQuestionFile = {
            fileIndex: fileIndex,
            canPrev: fileIndex > 0 ? true : false,
            canNext: fileIndex + 1 < this.audit!.auditExplanations!.filter((x) => x.fileId != null).length ? true : false,
            file: this.audit!.auditExplanations!.filter((x) => x.fileId != null)[fileIndex],
        };
        if (this.currentQuestionFile) this.auditFileDetail.open(this.currentQuestionFile);
    }

    prevAuditFile() {
        let targetFile: AuditExplanationModel | null = this.audit!.auditExplanations!.filter((x) => x.fileId != null)[
            this.currentQuestionFile.fileIndex - 1
        ];
        if (targetFile) {
            this.currentQuestionFile.file = targetFile;
            this.currentQuestionFile.fileIndex--;
            this.currentQuestionFile.canPrev = this.currentQuestionFile.fileIndex > 0 ? true : false;
            this.currentQuestionFile.canNext = true;
        }
    }

    nextAuditFile() {
        let targetFile: AuditExplanationModel | null = this.audit!.auditExplanations!.filter((x) => x.fileId != null)[
            this.currentQuestionFile.fileIndex + 1
        ];
        if (targetFile) {
            this.currentQuestionFile.file = targetFile;
            this.currentQuestionFile.fileIndex++;
            this.currentQuestionFile.canPrev = this.currentQuestionFile.fileIndex > 0 ? true : false;
            this.currentQuestionFile.canNext =
                this.currentQuestionFile.fileIndex + 1 < this.audit!.auditExplanations!.filter((x) => x.fileId != null).length
                    ? true
                    : false;
        }
    }

    validateMissingFiles() {
        let missingFilesCount = 0;
        this.items.forEach((x) => {
            x.questionContainers.forEach((qc) => {
                qc.question.isFileRequired &&
                    qc.responses.find((response) => {
                        if (!response.auditFiles || response.auditFiles!.length == 0) {
                            if (
                                response.okValue != null ||
                                response.scoreValue != null ||
                                response.systemAuditValue != null ||
                                response.productAuditValue != null
                            ) {
                                missingFilesCount++;
                            }
                        }
                    });
            });
        });
        return missingFilesCount > 0;
    }

    validate() {
        return this.form.validate();
    }

    getResponses(isCorrection = false): Array<ResponseModel> {
        const responses: Array<ResponseModel> = [];
        for (const item of this.items) {
            if (item.questionContainers) {
                for (const questionContainer of item.questionContainers) {
                    if (questionContainer.responses) {
                        questionContainer.responses.forEach((n: any) => {
                            const e = cloneDeep(n);
                            e.description = e.description ?? '';
                            let hasDisagreement = false;
                            if (!this.isRequiredFinding(e) && !this.audit?.isAllowedEmptyFindingDescription) {
                                e.findings = e.findings
                                    .where((f: any) => f.description != null && f.description != '')
                                    .toArray();
                            } else if (
                                this.auditModel.useIssueSolvingEscalation &&
                                (this.category!.processType == ProcessTypeEnum.Maintenance ||
                                    this.category!.processType == ProcessTypeEnum.Checklist ||
                                    this.category!.processType == ProcessTypeEnum.ProductInspection) &&
                                this.useAutoIssueEscalation(n)
                            ) {
                                e.findings = [];
                                hasDisagreement = this.isRequiredFinding(e);
                            }
                            if (this.isWithoutFinding(e)) {
                                e.findings = [];
                            }
                            e.sequenceIndex = item.questionContainers.indexOf(questionContainer) + 1;
                            e.responseFiles = [];
                            e.auditFiles.forEach((f: any) => {
                                e.responseFiles.push(f);
                            });
                            e.correctiveMeasureFiles.forEach((f: any) => {
                                e.responseFiles.push(f);
                            });
                            e.correctiveMeasureImplementationFiles.forEach((f: any) => {
                                e.responseFiles.push(f);
                            });
                            if (e.isOk == true) {
                                e.escalationDescription = '';
                                e.implementerUserId = null;
                                e.implementationState = ImplementationStateEnum.None;
                                e.implementationTime = null;
                            }
                            if (!this.item!.responseLock || isCorrection) {
                                if (e.isOk == false) {
                                    hasDisagreement = true;
                                } else if (e.immediateMeasures != null) {
                                    if (this.category?.processType == ProcessTypeEnum.Audit) {
                                        hasDisagreement = true;
                                    } else {
                                        hasDisagreement = e.findings.length > 0;
                                    }
                                } else if (e.findings.length == 0 && !this.auditModel.useIssueSolvingEscalation) {
                                    hasDisagreement = false;
                                }
                            }
                            e.hasDisagreement = hasDisagreement;
                            e.findings = e.findings.toArray();
                            for (const finding of e.findings) {
                                finding.description = finding.description ?? '';
                                finding.sequenceIndex = e.findings.indexOf(finding) + 1;
                                if (!finding.useDefinedManagers) {
                                    finding.findingManagers = [];
                                }
                            }
                            responses.push(e);
                        });
                    }
                }
            }
        }
        return responses;
    }

    public getPlanItemFindingManagers(): PlanItemFindingManagerModel[] {
        return this.findingManagers ?? [];
    }

    addFinding(response: ResponseModel) {
        response.findings!.push({
            id: Guid.EmptyGuid(),
            description: null,
            cause: null,
            useDefinedManagers: this.category && this.category.mustSelectFindingManager,
            findingManagers: [],
            findingFiles: [],
            processUntil:
                this.category?.useFindingProcessUntil && this.category.findingDefaultProcessDays
                    ? moment().add(this.category.findingDefaultProcessDays, 'days').format('YYYY-MM-DDTHH:mm:ss')
                    : null,
        } as FindingModel);
    }

    getNotFinishedResponses(): Array<ResponseModel> {
        let notFinishedResponses: Array<ResponseModel> = [];
        for (const item of this.items) {
            if (item.questionContainers) {
                for (const questionContainer of item.questionContainers) {
                    if (questionContainer.responses) {
                        questionContainer.responses.forEach((e: any) => {
                            if (
                                e.okValue == null &&
                                e.scoreValue == null &&
                                e.systemAuditValue == null &&
                                e.productAuditValue == null
                            ) {
                                notFinishedResponses.push(e);
                            } else if (e.okValue != null) {
                                if (!this.isValidByTimeLimit(e.okValue!)) {
                                    notFinishedResponses.push(e);
                                }
                            }
                        });
                    }
                }
            }
        }
        return notFinishedResponses;
    }

    isValid() {
        return this.valid;
    }

    hasChanges(): boolean {
        return !isEqual(this.itemsOriginal, this.items);
    }

    categoryCorrectionManagers() {
        const correctionManagers =
            this.category?.correctionManagers
                ?.where((x) => x.tenantUser != null && x.tenantUser.userId != null)
                .select((x) => x.tenantUser!)
                .toArray() ?? [];

        // Tenant users s oprávněním FindingManager
        const allTenantUsers = (tenantUsersStore.useGetter(CrudGetter.Data) as TenantUserModel[]) ?? [];
        const tenantUserById = new Map<string, TenantUserModel>(allTenantUsers.filter((u) => !!u.id).map((u) => [u.id!, u]));
        const findingManagerUsers = allTenantUsers.filter(
            (u) =>
                u.isActive == true &&
                (u.userRoles?.any(
                    (r) => r.role?.permissions?.any((p) => p.permissionType == PermissionTypeEnum.FindingManager) ?? false
                ) ??
                    false)
        );

        // Sjednocení bez duplicit dle id
        let union = [...correctionManagers, ...findingManagerUsers]
            .groupBy((x) => x.id!)
            .select((g) => g.first())
            .toArray();

        // Filtrování podle přístupu ke kategorii (uživatel má kategorii nebo přístup ke všem)
        const categoryId = this.category?.id ?? null;
        if (categoryId) {
            union = union.filter((u) => {
                const storeUser = u?.id ? tenantUserById.get(u.id!) : null;
                if (!storeUser) return true;
                return (
                    storeUser.categories?.any((c: any) => c.categoryId == categoryId) == true ||
                    storeUser.accessAllCategories == true
                );
            });
        }

        // Filtrování podle lokace pracoviště v rámci auditu
        const workplaceId = this.item?.workplaceId ?? null;
        if (!workplaceId) return union;

        const workplaces = (workplacesStore.useGetter(CrudGetter.Data) ?? []) as any[];
        const workplace = (workplaces as any).firstOrDefault
            ? (workplaces as any).firstOrDefault((w: any) => w.id == workplaceId)
            : workplaces.find((w: any) => w.id == workplaceId);
        const workplaceLocationId: string | null = workplace?.locationId ?? null;
        if (!workplaceLocationId) return union;

        const allLocations = (locationsStore.useGetter(CrudGetter.Data) ?? []) as any[];

        // Zjistit všechny nadřazené lokace (včetně vlastní) podle LocationParentModel
        const idToLocation = new Map<string, any>(allLocations.map((l: any) => [l.id, l]));
        const computeAncestorsInclusive = (startId: string): Set<string> => {
            const result = new Set<string>();
            const stack: string[] = [startId];
            while (stack.length > 0) {
                const currentId = stack.pop()!;
                if (result.has(currentId)) continue;
                result.add(currentId);
                const loc = idToLocation.get(currentId);
                const parents = ((loc?.locationParents ?? []) as any[])
                    .map((p: any) => p.parentalLocationId)
                    .filter((x: any) => !!x);
                for (const pid of parents) {
                    if (!result.has(pid)) stack.push(pid);
                }
            }
            return result;
        };

        const workplaceScope = computeAncestorsInclusive(workplaceLocationId);

        // Uživatel je viditelný, pokud nemá definované lokace, nebo některá z jeho lokací je potomkem (včetně) lokace pracoviště
        union = union.filter((u) => {
            const userLocations = (u as any).locations as { locationId?: string | null }[] | null | undefined;
            if (!userLocations || userLocations.length === 0) return true;
            // Uživatel smí, pokud má definovanou lokaci, která je nadřazená (nebo stejná) jako lokace pracoviště
            return userLocations.some((l) => !!l.locationId && workplaceScope.has(l.locationId!));
        });

        return union;
    }

    findingManagersChanged(finding: FindingModel, e: string[]) {
        const findingManagersToDeleted =
            finding.findingManagers?.where((x) => !e.any((n) => n == x.tenantUserId)).toArray() ?? [];
        for (const item of findingManagersToDeleted) {
            finding.findingManagers!.splice(finding.findingManagers!.indexOf(item), 1);
        }
        const findingManagersToAdd = e.where((x) => !finding.findingManagers!.any((n) => n.tenantUserId == x)).toArray() ?? [];
        for (const item of findingManagersToAdd) {
            finding.findingManagers!.push({
                id: Guid.EmptyGuid(),
                findingId: finding.id,
                tenantUserId: item,
            } as FindingManagerModel);
        }
    }

    getFindingManagersValue(finding: FindingModel) {
        return finding?.findingManagers?.select((x) => x.tenantUserId!).toArray() ?? [];
    }

    tenantUserLabel = (u: TenantUserModel) => {
        const name = u?.user?.displayName ?? '';
        const dept = u?.department?.name ? ` (${u.department.name})` : '';
        // Najít názvy lokací uživatele
        const allLocations = (locationsStore.useGetter(CrudGetter.Data) ?? []) as any[];
        const locationNames: string[] = (u?.locations ?? [])
            .map((x) => x.locationId)
            .filter((id): id is string => !!id)
            .map((id) => {
                const loc = (allLocations as any).firstOrDefault
                    ? (allLocations as any).firstOrDefault((l: any) => l.id == id)
                    : allLocations.find((l: any) => l.id == id);
                return loc?.name ?? '';
            })
            .filter((x) => x && x.length > 0)
            .sort((a, b) => a.localeCompare(b));
        const locPart = locationNames.length > 0 ? ` (${locationNames.join(', ')})` : '';
        return `${name}${dept}${locPart}`;
    };

    protected isProductAuditAnswerDisabled(response: ResponseContainer, responseValue: ResponseValueModel) {
        const isAllAnswered = response.responseParts!.filter((x) => x.isOk == null).length == 0;
        const isAllOk = response.responseParts!.filter((x) => x.isOk != true).length == 0;
        if (response.productAuditValue == -1) {
            return true;
        }
        if (!isAllAnswered) {
            return true;
        } else {
            if (responseValue?.value == 0 && !isAllOk) {
                return true;
            } else if ((responseValue.value ?? -1) >= 1 && (responseValue.value ?? -1) <= 3 && isAllOk) {
                return true;
            }
        }
        return false;
    }

    protected onProductAuditPartResponseChange(response: ResponseContainer) {
        const isAllAnswered = response.responseParts!.filter((x) => x.isOk == null).length == 0;
        const isAllOk = response.responseParts!.filter((x) => x.isOk != true).length == 0;
        if (isAllAnswered) {
            if (isAllOk && response.productAuditValue != 0) {
                response.productAuditValue = 0;
                this.responseChanged(response);
            } else if (
                response.productAuditValue != null &&
                (response.productAuditValue < 1 || response.productAuditValue > 3)
            ) {
                response.productAuditValue = null;
                this.responseChanged(response);
            }
        } else if (response.productAuditValue != null) {
            response.productAuditValue = null;
            this.responseChanged(response);
        }
    }

    private loadQuestionGroups(): Promise<any> {
        return new Promise((resolve, reject) => {
            questionGroupsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<QuestionGroupModel>(
                    {
                        field: 'position',
                        index: 1,
                        order: 'asc',
                    },
                    [{ field: 'auditId', op: 'eq', comparand: this.audit!.id! }],
                    undefined,
                    false,
                    'WorkTaskDialog'
                )
            );
            this.subscribe(questionGroupsStore, CrudReponse.GetAll, 'WorkTaskDialog')
                .then((e: QuestionGroupModel[]) => {
                    e.forEach((g) => {
                        if (g.questions) g.questions.sort((a, b) => a.position! - b.position!);
                    });
                    this.questionGroups = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadAuditWorkplaces(): Promise<any> {
        return new Promise((resolve, reject) => {
            auditWorkplacesStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<QuestionGroupModel>(
                    undefined,
                    [{ field: 'auditId', op: 'eq', comparand: this.audit!.id! }],
                    undefined,
                    false,
                    'WorkTaskDialog'
                )
            );
            this.subscribe(auditWorkplacesStore, CrudReponse.GetAll, 'WorkTaskDialog')
                .then((e: AuditWorkplaceModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadDefectsAndComponents(): void {
        if (!this.item?.workplaceId) return;

        // Načíst defekty pro pracoviště
        defectWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<DefectWorkplaceModel>(undefined, [
                { field: 'workplaceId', op: 'eq', comparand: this.item.workplaceId },
            ])
        );

        // Načíst komponenty pro pracoviště
        machineComponentWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<MachineComponentWorkplaceModel>(undefined, [
                { field: 'workplaceId', op: 'eq', comparand: this.item.workplaceId },
            ])
        );

        // Pokud je vybrané stanoviště, načíst také data pro stanoviště
        if (this.item.stationId) {
            defectStationsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<DefectStationModel>(undefined, [
                    { field: 'stationId', op: 'eq', comparand: this.item.stationId },
                ])
            );

            machineComponentStationsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<MachineComponentStationModel>(undefined, [
                    { field: 'stationId', op: 'eq', comparand: this.item.stationId },
                ])
            );
        }
    }

    private make() {
        this.items = this.questionGroups
            .where(
                (q) =>
                    (this.item != null &&
                        this.item.responses &&
                        this.item.responses.any((x) => x.question != null && x.question.questionGroupId == q.id)) ||
                    (this.item != null && !this.item.isAnswered)
            )
            .toArray()
            .map((e) => {
                const questionContainers: (QuestionContainer | null)[] = e.questions
                    ? e.questions
                          .where(
                              (q) =>
                                  this.item != null &&
                                  ((this.item.responses && this.item.responses.any((x) => x.questionId == q.id)) ||
                                      !this.item.isAnswered)
                          )
                          .toArray()
                          .map((q) => {
                              if (this.item && this.item.responses) {
                                  let responseContainers: ResponseContainer[] = [];
                                  if (this.isVda63) {
                                      const processElement = VDA63.ProcessElementsSteps.first(
                                          (x) => x.Element == e.processElement
                                      );
                                      let questions: Vda63QuestionModel[] = [];
                                      if (processElement.UseSubElements == true) {
                                          questions = processElement.SubElements.first(
                                              (x) => x.SubElement == e.processElementStep
                                          ).Questions;
                                      } else {
                                          questions = processElement.Questions;
                                      }
                                      const vdaQuestion = questions.first((x) => x.QuestionCode == q.code);
                                      if (vdaQuestion.ProcessStepType == ProcessStepTypeEnum.None) {
                                          const response = this.item.responses.firstOrDefault(
                                              (x) => x.questionId == q.id
                                          ) as ResponseModel;
                                          responseContainers = [this.getResponseContainer(q, response, null)];
                                      } else if (vdaQuestion.ProcessStepType == ProcessStepTypeEnum.UseDefined) {
                                          const effectiveSteps = ((this.item as any)?.planItemProcessSteps ?? [])
                                              .map((p: any) => p.processStep)
                                              .filter((x: any) => x && x.isHidden != true);
                                          const stepsSource =
                                              (effectiveSteps?.length ?? 0) > 0
                                                  ? effectiveSteps
                                                  : this.audit!.processSteps!.where((x) => !x.isHidden).toArray();
                                          responseContainers = stepsSource
                                              .orderBy((x: any) => x.position)
                                              .select((s: any) => {
                                                  const response = this.item!.responses!.firstOrDefault(
                                                      (x) => x.questionId == q.id && x.processStepId == s.id
                                                  ) as ResponseModel;
                                                  return this.getResponseContainer(q, response, s);
                                              })
                                              .toArray();
                                      } else if (vdaQuestion.ProcessStepType == ProcessStepTypeEnum.ProductAndProcess) {
                                          const productStep = this.audit!.processSteps!.first(
                                              (x) => x.isHidden == true && x.stepNumber == 'Product'
                                          );
                                          const processStep = this.audit!.processSteps!.first(
                                              (x) => x.isHidden == true && x.stepNumber == 'Process'
                                          );
                                          const productResponse = this.item!.responses!.firstOrDefault(
                                              (x) => x.questionId == q.id && x.processStepId == productStep.id
                                          ) as ResponseModel;
                                          const processResponse = this.item!.responses!.firstOrDefault(
                                              (x) => x.questionId == q.id && x.processStepId == processStep.id
                                          ) as ResponseModel;
                                          responseContainers = [
                                              this.getResponseContainer(q, productResponse, productStep),
                                              this.getResponseContainer(q, processResponse, processStep),
                                          ];
                                      } else if (vdaQuestion.ProcessStepType == ProcessStepTypeEnum.Process) {
                                          const processStep = this.audit!.processSteps!.first(
                                              (x) => x.isHidden == true && x.stepNumber == 'Process'
                                          );
                                          const processResponse = this.item!.responses!.firstOrDefault(
                                              (x) => x.questionId == q.id && x.processStepId == processStep.id
                                          ) as ResponseModel;
                                          responseContainers = [this.getResponseContainer(q, processResponse, processStep)];
                                      }
                                  } else {
                                      const response = this.item.responses.firstOrDefault(
                                          (x) => x.questionId == q.id
                                      ) as ResponseModel;
                                      responseContainers = [this.getResponseContainer(q, response, null)];
                                  }
                                  if (this.isProductAudit) {
                                      const response = this.item.responses.firstOrDefault(
                                          (x) => x.questionId == q.id
                                      ) as ResponseModel;
                                      const existingResponseParts = responseContainers.first().responseParts!.toArray();
                                      responseContainers[0].responseParts = this.audit!.parts!.select((x) => {
                                          if (response) {
                                              const responsePart = response.responseParts?.firstOrDefault(
                                                  (t) => t.partId === x.id
                                              );
                                              if (responsePart) {
                                                  return responsePart;
                                              }
                                          }
                                          return (
                                              existingResponseParts.firstOrDefault((y) => y.partId == x.id) ?? {
                                                  partId: x.id,
                                                  part: x,
                                                  isOk: null,
                                              }
                                          );
                                      }).toArray();
                                  }
                                  return {
                                      id: q.id,
                                      questionId: q.id,
                                      question: q,
                                      position: q.position,
                                      responses: responseContainers,
                                  } as QuestionContainer;
                              } else {
                                  return null;
                              }
                          })
                          .where((x) => x != null)
                          .toArray()
                    : [];
                const n = {
                    id: e.id,
                    auditId: e.auditId,
                    name: e.name,
                    position: e.position,
                    processElement: e.processElement,
                    processElementStep: e.processElementStep,
                    questionContainers: questionContainers,
                } as QuestionGroupResponse;
                return n;
            }) as Array<QuestionGroupResponse>;
        this.itemsOriginal = cloneDeep(this.items);
        // TEST ONLY: assign default score 10 for VDA 6.3 into constructed items
        try {
            if (this.isVda63) {
                this.items.forEach((group) => {
                    group.questionContainers.forEach((qc) => {
                        qc.responses.forEach((r) => {
                            if (r.scoreValue == null) {
                                r.scoreValue = 10;
                            }
                        });
                    });
                });
            }
        } catch (e) {
            console.warn('applyVda63TestDefaultScore(items) failed', e);
        }
        this.findingManagers = [...(this.item?.findingManagers ?? [])];
    }

    private getResponseContainer(question: QuestionModel, response: ResponseModel, processStep: ProcessStepModel | null) {
        if (response !== null) {
            const responseObject = { ...response } as ResponseContainer;
            responseObject.findings = responseObject.findings?.orderBy((x: FindingModel) => x.sequenceIndex).toArray() ?? [];
            responseObject.auditFiles =
                response.responseFiles?.where((f: ResponseFileModel) => f.type == ResponseFileType.Response).toArray() ?? [];
            responseObject.correctiveMeasureFiles =
                response.responseFiles
                    ?.where((f: ResponseFileModel) => f.type == ResponseFileType.CorrectiveMeasure)
                    .toArray() ?? [];
            responseObject.correctiveMeasureImplementationFiles =
                response.responseFiles
                    ?.where((f: ResponseFileModel) => f.type == ResponseFileType.CorrectiveMeasureImplementation)
                    .toArray() ?? [];
            responseObject.responseParts = response.responseParts ?? [];
            return responseObject;
        } else {
            return {
                id: Guid.EmptyGuid(),
                isOk: null,
                questionId: question.id,
                question: question,
                auditFiles: [],
                correctiveMeasureFiles: [],
                correctiveMeasureImplementationFiles: [],
                okValue: null,
                systemAuditValue: null,
                scoreValue: null,
                productAuditValue: null,
                immediateMeasures: null,
                escalationDescription: null,
                description: null,
                findings: [],
                notRatedReason: null,
                processStepId: processStep?.id,
                processStep: processStep,
                responseParts: [],
            } as ResponseContainer;
        }
    }

    protected getRelations(response: ResponseContainer): PlanItemRelationModel[] {
        return this.item?.relations?.filter((x) => x.responseId == response.id) ?? [];
    }

    private isRequiredFinding(response: ResponseModel): boolean {
        let responseValue: ResponseValueModel | null = null;
        if (
            this.category!.responseType == ResponseTypeEnum.Ok ||
            this.category!.responseType == ResponseTypeEnum.OkNok ||
            this.category!.responseType == ResponseTypeEnum.OkNokWarning ||
            this.category!.responseType == ResponseTypeEnum.NokWarning
        ) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.okValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.systemAuditValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.productAuditValue) ?? null;
        } else {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.scoreValue) ?? null;
        }
        if (responseValue == null) {
            return false;
        } else if (responseValue?.escalationType == EscalationTypeEnum.RequiredEscalation) {
            return true;
        } else if (responseValue?.escalationType == EscalationTypeEnum.OptionalEscalation) {
            return false;
        } else {
            return false;
        }
    }

    private isWithoutFinding(response: ResponseModel): boolean {
        let responseValue: ResponseValueModel | null = null;
        if (
            this.category!.responseType == ResponseTypeEnum.Ok ||
            this.category!.responseType == ResponseTypeEnum.OkNok ||
            this.category!.responseType == ResponseTypeEnum.OkNokWarning ||
            this.category!.responseType == ResponseTypeEnum.NokWarning
        ) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.okValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.systemAuditValue) ?? null;
        } else if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.productAuditValue) ?? null;
        } else {
            responseValue = this.category!.responseValues?.firstOrDefault((x) => x.value == response.scoreValue) ?? null;
        }
        if (responseValue == null) {
            return false;
        } else if (responseValue?.escalationType == EscalationTypeEnum.RequiredEscalation) {
            return false;
        } else if (responseValue?.escalationType == EscalationTypeEnum.OptionalEscalation) {
            return false;
        } else {
            return true;
        }
    }

    private getExplanations(questionGroup: QuestionGroupModel) {
        let explanations: any[] = [];
        if (this.item!.audit!.explanationType == AuditExplanationTypeEnum.ByCategory) {
            explanations = this.item!.audit!.category!.responseValues!.map((x) => {
                return {
                    value: x.value,
                    color: x.color,
                    explanationDescription: x.explanationDescription,
                };
            }).toArray();
        } else if (this.item!.audit!.explanationType == AuditExplanationTypeEnum.ByAudit) {
            explanations = this.item!.audit!.category!.responseValues!.map((x) => {
                const auditExplanation = this.audit!.auditExplanations!.firstOrDefault((n) => n.responseValue == x.value);
                return {
                    value: x.value,
                    color: x.color,
                    explanationDescription: auditExplanation?.explanationDescription,
                };
            }).toArray();
        } else if (this.item!.audit!.explanationType == AuditExplanationTypeEnum.ByQuestionGroup) {
            explanations = this.item!.audit!.category!.responseValues!.map((x) => {
                const filteredQuestionGroup = this.questionGroups.first((n) => n.id == questionGroup.id);
                const questionGroupExplanation = filteredQuestionGroup!.questionGroupExplanations?.firstOrDefault(
                    (n) => n.responseValue == x.value
                );
                return {
                    value: x.value,
                    color: x.color,
                    explanationDescription: questionGroupExplanation?.explanationDescription,
                };
            }).toArray();
        }
        if (!explanations.any((x) => x.explanationDescription != null && x.explanationDescription.toString().length > 0)) {
            return [];
        } else {
            return explanations;
        }
    }

    private getFileExplanations(): FileModel[] {
        if (this.audit?.explanationType == AuditExplanationTypeEnum.ByAudit) {
            return this.audit!.auditExplanations!.filter((x) => x.file).map((x) => x.file!);
        }
        return [];
    }

    private canShowExplanations(questionGroup: QuestionGroupModel) {
        if (this.audit?.explanationType == AuditExplanationTypeEnum.None || this.audit?.explanationType == null) return false;

        return this.getExplanations(questionGroup).length > 0 || this.getFileExplanations().length > 0;
    }

    protected getFindingManagerTenantUserList(finding: FindingModel): string[] {
        return finding?.findingManagers?.map((x) => x.tenantUserId!) ?? [];
    }

    protected setFindingManagerTenantUserList(finding: FindingModel, tenantUsers: string[]) {
        if (!finding) {
            return;
        }
        finding.useDefinedManagers = true;
        if (!finding.findingManagers) {
            finding.findingManagers = [];
        }
        const existingTenantUsers = finding.findingManagers.map((x) => x.tenantUserId!);
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            finding.findingManagers.push({
                tenantUserId: tenantUser,
            });
        }
        for (const tenantUser of tenantUsersToDelete) {
            const findingManager = finding.findingManagers.find((x) => x.tenantUserId! == tenantUser)!;
            finding.findingManagers.splice(finding.findingManagers.indexOf(findingManager), 1);
        }
    }

    protected getPlanItemFindingManagerTenantUserList(): string[] {
        return this.findingManagers.map((x) => x.tenantUserId!) ?? [];
    }

    protected setPlanItemFindingManagerTenantUserList(tenantUsers: string[]) {
        const existingTenantUsers = this.findingManagers.map((x) => x.tenantUserId!);
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            this.findingManagers.push({
                tenantUserId: tenantUser,
            });
        }
        for (const tenantUser of tenantUsersToDelete) {
            const findingManager = this.findingManagers.find((x) => x.tenantUserId! == tenantUser)!;
            this.findingManagers.splice(this.findingManagers.indexOf(findingManager), 1);
        }
    }

    protected editShutdownFromDateClick() {
        this.editShutdownDialog.open(
            this.id,
            this.$t('planItems.shutdownStart').toString(),
            'shutdownFromDate',
            this.item?.shutdownFromDate,
            null,
            this.item?.shutdownToDate
        );
    }

    protected editShutdownToDateClick() {
        this.editShutdownDialog.open(
            this.id,
            this.$t('planItems.shutdownEnd').toString(),
            'shutdownToDate',
            this.item?.shutdownToDate,
            this.item?.shutdownFromDate,
            null
        );
    }

    protected getResponseValueLabel(responseValue: number): string | null {
        return this.getResponseValue(responseValue)?.label ?? null;
    }

    protected getResponseValue(responseValue: number): ResponseValueModel | null {
        return this.audit?.category?.responseValues?.find((x) => x.value == responseValue) ?? null;
    }

    protected isValidByTimeLimit(responseValue: number): boolean {
        const planItem = planItemsStore.useGetter(CrudGetter.Detail) as PlanItemModel;
        if (!planItem) {
            return true;
        }
        const timeLimit = (this.getResponseValue(responseValue)?.timeLimitFromStart ?? 10512000) * 60000;
        return new Date(planItem.createdAt!).getTime() + timeLimit > new Date().getTime();
    }

    protected planItemChanged() {
        this.$emit('planItemChanged');
        this.refresh();
    }

    protected useAutoIssueEscalation(response: ResponseContainer) {
        console.log(
            response.question?.escalationAuditId != null,
            response.question?.escalationFaultTypeId != null,
            response.question?.escalationGroupId != null
        );
        return (
            response.question?.escalationAuditId != null &&
            response.question?.escalationFaultTypeId != null &&
            response.question?.escalationGroupId != null
        );
    }

    onDeletePlanItemClick() {
        this.$emit('deletePlanItem');
    }
}
